import * as _ from 'lodash';

import { plural } from '../../../../../shared/helpers/plural';
import { getAuthenticationMode } from '../../../../../shared/organizations/utils';
import { OrganizationSsoMethod } from '../../../../../typings/enums';
import type { OrganizationDomain } from '../../../../../shared/model-typings';
import type { ConfirmDialogProps, DialogItemProps } from '../../../../contexts/DialogContext';
import type { LDAPStrategy, SamlStrategy } from '../../../../types';

import { defaultLDAPConfig } from './SSOConfiguration/LDAP/ldapConfigHelpers';
import { AuthenticationMode } from './types';
import type { AuthenticationSettingsWithPasswords } from './types';

function organizationHasGoogleConfigured(orgDomains: OrganizationDomain[]): boolean {
  return orgDomains.some(({ ssoMethod }) => ssoMethod === OrganizationSsoMethod.Google);
}

function organizationHasLDAPConfigured(organization: AuthenticationSettingsWithPasswords): boolean {
  return !!organization.ldap?.enabled;
}

function organizationHasSAMLConfigured(organization: AuthenticationSettingsWithPasswords): boolean {
  return !!organization.saml?.entryPoint && !!organization.saml?.cert;
}

/**
 * Returns the authentication mode for the organization.
 */
export function getAuthenticationModeFromSettings(
  settings: AuthenticationSettingsWithPasswords,
  orgDomains: OrganizationDomain[],
): AuthenticationMode {
  return getAuthenticationMode({
    isSSORequired: settings.disableInvite,
    hasSAMLConfigured: organizationHasSAMLConfigured(settings),
    hasLDAPConfigured: organizationHasLDAPConfigured(settings),
    hasGoogleConfigured: organizationHasGoogleConfigured(orgDomains),
  });
}

const authModesWithSSO = new Set([AuthenticationMode.SsoEmail, AuthenticationMode.Sso]);

/**
 * Returns the authentication method for the organization.
 */
const ssoMethodName = (method: OrganizationSsoMethod): string => {
  switch (method) {
    case OrganizationSsoMethod.Google:
      return 'Google';
    case OrganizationSsoMethod.SAML:
      return 'SAML';
    case OrganizationSsoMethod.LDAP:
      return 'LDAP';
    default:
      throw new Error(`Unknown SSO method: ${String(method)}`);
  }
};

/**
 * Switches the SSO configuration for the organization.
 */
export async function switchSSO({
  settings,
  currentMethod,
  currentMode,
  newMethod,
  newMode,
  claimedDomains,
  onConfirm,
  setMethod,
  setMode,
  deleteDomain,
  deleteSaml,
  saveLDAP,
  onUpdateLDAP,
  onUpdateSAML,
}: {
  settings: AuthenticationSettingsWithPasswords;
  currentMode: AuthenticationMode;
  newMode: AuthenticationMode;
  currentMethod: OrganizationSsoMethod;
  newMethod: OrganizationSsoMethod;
  claimedDomains: OrganizationDomain[];
  onConfirm: (state: DialogItemProps<ConfirmDialogProps>) => void;
  setMode: (mode: AuthenticationMode) => void;
  setMethod: (method: OrganizationSsoMethod) => void;
  deleteDomain: (domain: string) => Promise<void>;
  deleteSaml: () => Promise<void>;
  saveLDAP: (newLdap: Partial<LDAPStrategy>) => Promise<{ data: Partial<LDAPStrategy> }>;
  onUpdateLDAP: (newLdap: Partial<LDAPStrategy>, isSilent: boolean) => void;
  onUpdateSAML: (newSaml: Partial<SamlStrategy>, isSilent: boolean) => void;
}) {
  const doesCurrentModeHaveSSO = authModesWithSSO.has(currentMode);
  const doesNewModeHaveSSO = authModesWithSSO.has(newMode);

  // If the mode is changed and the new mode does not have an SSO option while the old one did, or if the auth method is
  // changed, clear out all current SSO configuration.
  if ((doesCurrentModeHaveSSO && !doesNewModeHaveSSO) || currentMethod !== newMethod) {
    // So that we don't prompt the user to confirm whether they want to delete an SSO configuration that doesn't exist,
    // check if there is a configuration for the current method and if so override deleteFn.
    let deleteFn: (() => Promise<void | void[]>) | null = null;

    switch (currentMethod) {
      case OrganizationSsoMethod.Google:
        {
          const domainsToDelete = claimedDomains.filter(({ ssoMethod }) => ssoMethod === OrganizationSsoMethod.Google);

          if (domainsToDelete.length) {
            deleteFn = () => Promise.all(domainsToDelete.map(({ domain }) => deleteDomain(domain)));
          }
        }
        break;
      case OrganizationSsoMethod.SAML:
        // For SAML, prompt the user to confirm the action if either the entryPoint or the cert is set.
        if (settings.saml?.entryPoint || settings.saml?.cert) {
          deleteFn = async () => {
            await deleteSaml();
            onUpdateSAML({ cert: '', entryPoint: '' }, true);
          };
        }
        break;
      case OrganizationSsoMethod.LDAP:
        if (organizationHasLDAPConfigured(settings)) {
          deleteFn = async () => {
            const { data } = await saveLDAP(defaultLDAPConfig);
            onUpdateLDAP(data, true);
          };
        }
        break;
    }

    if (deleteFn) {
      const domainsToRemoveCount = _.sumBy(claimedDomains, ({ ssoMethod }) => (ssoMethod === currentMethod ? 1 : 0));
      let body = `This will permanently remove the ${ssoMethodName(currentMethod)} SSO configuration`;
      if (domainsToRemoveCount) {
        // If there is a domain claimed for the SSO method to be removed, mention this in the confirm dialog.
        body = `${body} and your claimed ${plural(domainsToRemoveCount, 'domain')}.`;
      } else {
        body = `${body}.`;
      }

      onConfirm({
        title: `Confirm remove ${ssoMethodName(currentMethod)} SSO configuration?`,
        body,
        onConfirm: async () => {
          await deleteFn?.();
          setMode(newMode);
          setMethod(newMethod);
        },
      });

      return;
    }
  }

  setMode(newMode);
  setMethod(newMethod);
}

/**
 * Returns the authentication method for the organization.
 */
export function getAuthenticationMethod(settings: AuthenticationSettingsWithPasswords): OrganizationSsoMethod {
  if (organizationHasSAMLConfigured(settings)) {
    return OrganizationSsoMethod.SAML;
  }

  if (organizationHasLDAPConfigured(settings)) {
    return OrganizationSsoMethod.LDAP;
  }

  return OrganizationSsoMethod.Google;
}

/**
 * Returns true if the subdomain is valid.
 */
export function isValidSubdomain(subdomain: string) {
  // Disallow empty subdomains.
  if (subdomain.length === 0) {
    return false;
  }

  // Subdomains permit hyphens but not at the start or end of the string.
  if (subdomain.charAt(0) === '-') {
    return false;
  }
  if (subdomain.charAt(subdomain.length - 1) === '-') {
    return false;
  }

  // Otherwise, ensure the subdomain is composed of only valid characters.
  return /^[a-z0-9-]+$/i.test(subdomain);
}
